<template>
  <Teleport to="body">
    <div v-if="state.isOpen" class="listing-detail listing-detail--projects">
      <div class="listing-detail__scrim" @click="close" />
      <div
        ref="panel"
        class="listing-detail__panel"
        role="dialog"
        aria-modal="true"
        :aria-label="t('project-detail.aria-label')"
      >
        <div class="listing-detail__media">
          <div
            v-if="images.length"
            ref="track"
            class="listing-detail__slider"
            @scroll.passive="onScroll"
          >
            <img
              v-for="(image, index) in images"
              :key="index"
              class="listing-detail__image"
              :src="image"
              alt=""
              :loading="index === 0 ? 'eager' : 'lazy'"
              draggable="false"
            />
          </div>
          <div v-else class="listing-detail__image listing-detail__image--empty">
            <SvgIcon name="home" :size="32" />
            <span class="listing-detail__no-image">{{ t('listing-card.no-image') }}</span>
          </div>

          <button
            ref="closeButton"
            type="button"
            class="listing-detail__close"
            :aria-label="t('listing-detail.close')"
            @click="close"
          >
            <SvgIcon name="x" :size="17" />
          </button>

          <!-- The chip from ProjectCard rather than the listing panel's status
               bar: the project status vocabulary is its own, and the dot colour
               is what carries it (WHN-2105). -->
          <span
            v-if="project.status"
            class="project-card__status listing-detail__status-chip"
            :data-status="project.status"
          >
            <span class="project-card__status-dot" />
            {{ project.status }}
          </span>

          <template v-if="images.length > 1">
            <button
              v-if="current > 0"
              type="button"
              class="listing-detail__nav listing-detail__nav--prev"
              :aria-label="t('listing-card.image-prev')"
              @click="goTo(current - 1)"
            >
              <SvgIcon name="chevron" :size="14" :stroke="2" />
            </button>
            <button
              v-if="current < images.length - 1"
              type="button"
              class="listing-detail__nav listing-detail__nav--next"
              :aria-label="t('listing-card.image-next')"
              @click="goTo(current + 1)"
            >
              <SvgIcon name="chevron" :size="14" :stroke="2" />
            </button>
            <div class="listing-detail__dots">
              <button
                v-for="(image, index) in images"
                :key="index"
                type="button"
                class="listing-detail__dot"
                :class="{ 'is-active': index === current }"
                :aria-label="t('listing-card.image-goto', { index: index + 1 })"
                @click="goTo(index)"
              />
            </div>
          </template>
        </div>

        <div class="listing-detail__scroll">
          <div class="listing-detail__body">
            <h2 class="listing-detail__title">{{ title }}</h2>
            <p v-if="locationLabel" class="listing-detail__subtitle">{{ locationLabel }}</p>

            <div class="listing-detail__actions">
              <a
                v-if="project.url"
                class="listing-detail__button listing-detail__button--primary"
                :href="project.url"
                target="_blank"
                rel="noopener"
              >{{ t('listing-detail.view-project') }}</a>
              <a
                v-if="followUrl"
                class="listing-detail__button listing-detail__button--secondary"
                :href="followUrl"
                target="_blank"
                rel="noopener"
              >{{ t('project-detail.follow-project') }}</a>
            </div>

            <div v-if="specs.length" class="listing-detail__specs">
              <dl class="listing-detail__specs-list listing-detail__specs-list--bare">
                <div v-for="spec in specs" :key="spec.label" class="listing-detail__spec">
                  <dt class="listing-detail__spec-label">
                    <!-- Same icons the project cards use for these specs, so
                         the panel reads as the card unfolded -->
                    <SvgIcon :name="spec.icon" :size="18" class="listing-detail__spec-icon" />
                    {{ spec.label }}
                  </dt>
                  <dd class="listing-detail__spec-value">{{ spec.value }}</dd>
                </div>
              </dl>
            </div>

            <p v-if="state.loading" class="listing-detail__loading">
              {{ t('project-detail.loading') }}
            </p>

            <!-- eslint-disable-next-line vue/no-v-html : sanitized CMS wysiwyg -->
            <div v-if="descriptionHtml" class="listing-detail__description" v-html="descriptionHtml" />

            <p v-if="state.error" class="listing-detail__error">{{ t('project-detail.error') }}</p>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import SvgIcon from './SvgIcon.vue';
import { useProjectDetail } from '../lib/useProjectDetail';
import { sanitizeHtml, stripTextColors, stripFontSizes, openLinksInNewTab } from '../lib/sanitizeHtml';
import { formatAreaRange } from '../lib/formatAreaRange';
import { t } from '../lib/i18n';

const { state, close } = useProjectDetail();

// The card/popup seed renders immediately; the fetched detail overrides and
// extends it as soon as it lands. Only the description arrives that way, so the
// panel is complete apart from one paragraph while it loads.
const project = computed(() => ({ ...(state.seed || {}), ...(state.detail || {}) }));

const images = computed(() =>
  (project.value.images || []).map((image) => image.url || image.src || image).filter(Boolean)
);

const panel = ref(null);
const closeButton = ref(null);
const track = ref(null);
const current = ref(0);

const onScroll = () => {
  const el = track.value;
  if (el) current.value = Math.round(el.scrollLeft / el.clientWidth);
};

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const goTo = (index) => {
  const el = track.value;
  if (!el) return;
  el.scrollTo({ left: index * el.clientWidth, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
};

// Project name and phase as one heading, the same reading order as ProjectCard
const title = computed(() =>
  [project.value.project, project.value.subProject].filter(Boolean).join(' – ')
);

const locationLabel = computed(() =>
  [project.value.city, project.value.region].filter(Boolean).join(', ')
);

// Following a project is a form the project page itself carries, in the modal
// ModalApp opens from ?show-modal (public-src/js/modal.js). Linking into it
// keeps one form and one project binding instead of a second copy in here.
const followUrl = computed(() => {
  const url = project.value.url;
  if (!url) return null;

  return `${url}${url.includes('?') ? '&' : '?'}show-modal=project-updates`;
});

// Same three specs the card shows, in the same order and with the same icons,
// so the panel reads as the card unfolded rather than as another view.
const specs = computed(() => {
  const { housingCount, livingAreaRange, parcelAreaRange, expectedHousingIndication } =
    project.value;
  const living = formatAreaRange(livingAreaRange);
  const parcel = formatAreaRange(parcelAreaRange);
  const homes = housingCount
    ? t(housingCount === 1 ? 'project-card.spec.home' : 'project-card.spec.homes', {
        count: housingCount
      })
    : expectedHousingIndication || null;

  return [
    homes ? { icon: 'home', label: t('project-detail.specs.homes'), value: homes } : null,
    living ? { icon: 'layers', label: t('listing-card.spec.living'), value: living } : null,
    parcel ? { icon: 'parcel', label: t('listing-card.spec.parcel'), value: parcel } : null
  ].filter(Boolean);
});

// The phase page's own intro paragraph (ProjectMapper::mapDetail). Editors put
// links in most of them; they stay live but open in a new tab, because the panel
// is a dialog over the result list.
const descriptionHtml = computed(() =>
  openLinksInNewTab(stripFontSizes(stripTextColors(sanitizeHtml(project.value.description))))
);

const focusables = () =>
  Array.from(
    panel.value?.querySelectorAll('a[href], button:not([disabled])') || []
  ).filter((el) => el.offsetParent !== null);

const onKeydown = (event) => {
  if (event.key === 'Escape') {
    close();
    return;
  }
  if (event.key !== 'Tab') return;

  const elements = focusables();
  if (!elements.length) return;
  const first = elements[0];
  const last = elements[elements.length - 1];

  if (event.shiftKey && (document.activeElement === first || !panel.value.contains(document.activeElement))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

let previousOverflow = '';

const activate = () => {
  document.addEventListener('keydown', onKeydown);
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  current.value = 0;
  nextTick(() => closeButton.value?.focus());
};

const deactivate = () => {
  document.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = previousOverflow;
};

watch(
  () => state.isOpen,
  (isOpen) => (isOpen ? activate() : deactivate())
);

onBeforeUnmount(() => {
  if (state.isOpen) deactivate();
});
</script>
